/**
 * Organization API Service
 *
 * Service functions for interacting with the organization API endpoints.
 */

import { apiClient } from "./api";
import type {
  Organization,
  OrganizationInvite,
  OrganizationMember,
  OrganizationCreateData,
  OrganizationUpdateData,
  OrganizationInviteCreateData,
  OrganizationInviteRespondData,
} from "@/types/organization";

const API_PREFIX = "/api/v1";

/**
 * Create a new organization.
 * The current user becomes the owner and is automatically linked to the org.
 */
export async function createOrganization(data: OrganizationCreateData): Promise<Organization> {
  return apiClient.post<Organization>(`${API_PREFIX}/organization`, data);
}

/**
 * Get the current user's organization.
 */
export async function getMyOrganization(): Promise<Organization> {
  return apiClient.get<Organization>(`${API_PREFIX}/organization/me`);
}

/**
 * Update an organization (owner only).
 */
export async function updateOrganization(
  orgId: number,
  data: OrganizationUpdateData
): Promise<{ message: string }> {
  return apiClient.patch<{ message: string }>(`${API_PREFIX}/organization/${orgId}`, data);
}

/**
 * Create an invite to the organization (owner only).
 */
export async function createOrganizationInvite(
  orgId: number,
  data: OrganizationInviteCreateData
): Promise<OrganizationInvite> {
  return apiClient.post<OrganizationInvite>(`${API_PREFIX}/organization/${orgId}/invites`, data);
}

/**
 * Get all invites for an organization (owner only).
 */
export async function getOrganizationInvites(orgId: number): Promise<OrganizationInvite[]> {
  return apiClient.get<OrganizationInvite[]>(`${API_PREFIX}/organization/${orgId}/invites`);
}

/**
 * Cancel a pending invite (owner only).
 */
export async function cancelOrganizationInvite(orgId: number, inviteId: number): Promise<void> {
  return apiClient.delete(`${API_PREFIX}/organization/${orgId}/invites/${inviteId}`);
}

/**
 * Get all members of an organization.
 */
export async function getOrganizationMembers(orgId: number): Promise<OrganizationMember[]> {
  return apiClient.get<OrganizationMember[]>(`${API_PREFIX}/organization/${orgId}/members`);
}

/**
 * Remove a member from the organization (owner only).
 */
export async function removeOrganizationMember(orgId: number, userId: number): Promise<void> {
  return apiClient.delete(`${API_PREFIX}/organization/${orgId}/members/${userId}`);
}

/**
 * Get pending invites for the current user.
 */
export async function getPendingInvites(): Promise<OrganizationInvite[]> {
  return apiClient.get<OrganizationInvite[]>(`${API_PREFIX}/invites/pending`);
}

/**
 * Respond to an invite (accept or decline).
 */
export async function respondToInvite(
  inviteId: number,
  data: OrganizationInviteRespondData
): Promise<OrganizationInvite> {
  return apiClient.post<OrganizationInvite>(`${API_PREFIX}/invites/${inviteId}/respond`, data);
}
